import React, { useEffect, useState, useRef, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CSSTransition } from 'react-transition-group';
import {
  loadMoreDeals,
  updateDeals,
  selectDealsList,
  clearNextDeals,
  selectBestOutOfDestinations,
} from './dealsListSlice';
import translate from '../../utilities/translator';
import { setShowLoader as setShowLoaderAction } from '../search/searchSlice';
import { pushEvent } from '../../utilities/analytics';
import { PAGINATION_CLICKED_EVENT } from '../../constants/analyticEvents';
import Button from '../../components/Button';
import DealsLoader from '../../components/Deals/DealsLoader';
import { setShouldScroll } from '../../appSlice';
import { selectFilterData } from '../mainFilter/selectors';
import ReactTooltip from '@huner2/react-tooltip';
import { isMobile } from '../../utilities/helpers';
import { scrollToResultsFast, multiplyBestOutOf } from '../../utilities/helpers';
import { getParamFromUrl } from '../../utilities/urlResolver';
import DealItemExperimental from './DealItemExperimental';
import DealOfTheDay from './DealOfTheDay';
import abTesting from '../../services/abTesting';
import MobileListVirtualized from './MobileListVirtualized';

const DealsList = () => {
  const dispatch = useDispatch();
  const mainFilter = useSelector(selectFilterData);
  const search = useSelector(state => state.userSearch);
  const secondaryFilter = useSelector(state => state.secondaryFilter.data);
  const priceGraphTop = useSelector(state => state.app.priceGraphTop);
  const shouldScroll = useSelector(state => state.app.shouldScroll);
  const educationalMessage = useSelector(state => state.app.educationalMessage);
  const { dealsList, pending, loadMorePending, error, pagination, nextDeals, dealOfTheDay, dotdPending } = useSelector(selectDealsList);
  const selectedHotel = useSelector(state => state.userSearch.selectedHotel);
  const showOnboarding = useSelector(state => state.onboarding.show);
  const { where, what, budget, when, who } = mainFilter.searchEngine;
  const dealListLoading = useSelector(state => state.app.globalLoading.dealListLoading);
  const filtersInteractionCount = useSelector(state => state.counters.filtersInteractionCount);
  const bestOutOfDestinations = useSelector(selectBestOutOfDestinations);

  const [showLoader, setShowLoader] = useState(true);
  const [countPage, setCountPage] = useState(2);
  const [isFetching, setIsFetching] = useState(false);
  const showLoadMoreBtn =
    !priceGraphTop &&
    (pagination.current_offers_page < pagination.total_offers_pages || nextDeals?.length > 0) &&
    !pending &&
    !loadMorePending;

  // best out of logic
  const bestOutOfMultiplied = multiplyBestOutOf(when, 'multiplyDestinations', bestOutOfDestinations);

  // const [lowestPriceFirst3, setLowestPriceFirst3] = useState(0);

  useEffect(() => {
    if (shouldScroll) {
      scrollToResultsFast();
    }
  }, [shouldScroll]);

  const userProfileClean = () => {
    // remove old items from usersProfileObj in localStorage
    const usersProfileObj = JSON.parse(localStorage.getItem('usersProfileObj'));
    if (usersProfileObj) {
      const timeNow = Date.now();
      const usersProfileObjNew = {};
      Object.entries(usersProfileObj).forEach((el) => {
        if (el[1].timestamp) {
          const daysDifference = Math.floor((timeNow - el[1].timestamp) / (1000 * 60 * 60 * 24));
          if (daysDifference < 31) {
            usersProfileObjNew[el[0]] = el[1];
          }
        }
      });
      localStorage.setItem('usersProfileObj', JSON.stringify(usersProfileObjNew));
    }
  };

  const scrolltoRef = useRef();
  const getOffsetTopToFetch = () => {
    if (scrolltoRef && scrolltoRef.current) {
      return scrolltoRef.current.offsetTop - 500;
    }
    return document.scrollingElement.scrollHeight - 500;
  };

  useEffect(() => {
    const handleScroll = () => {
      // infinite scroll for mobile only
      if (!isMobile()) {
        return;
      }
      if (
        showLoadMoreBtn &&
        !isFetching &&
        window.innerHeight + document.documentElement.scrollTop >= getOffsetTopToFetch()
      ) {
        setIsFetching(true);
      } else {
        return;
      }
    };

    if (isMobile()) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showLoadMoreBtn, isFetching]);

  useEffect(() => {
    if (isFetching && showLoadMoreBtn && !showLoader && !loadMorePending) {
      onLoadMoreClick();
      setIsFetching(false);
    }
  }, [isFetching, showLoader, loadMorePending, showLoadMoreBtn]);

  useEffect(() => {
    userProfileClean();
  }, []);

  useEffect(() => {
    ReactTooltip.rebuild();
  }, [dealsList]);

  /* useEffect(() => {
    let lowestPriceFirst3t = 0;
    if (abTesting.dealOfTheDay && dealsList?.length > 2 && dealOfTheDay?.length) {
      // the lowest price of the first three offers. it's needed to compare with DOTD price
      lowestPriceFirst3t = [...dealsList].slice(0, 3).map(of => of.offer.price).reduce((a, b) => Math.min(a, b));
      setLowestPriceFirst3(lowestPriceFirst3t);
      if (lowestPriceFirst3t < dealOfTheDay[0]?.offer?.price) {
        console.log(`Deal of the day (DOTD) skipped, it's more expensive (${dealOfTheDay[0].offer.price}) then the prev offers in that row (${lowestPriceFirst3t})`);
      }
    }
  }, [dealsList, dealOfTheDay]); */

  useEffect(() => {
    const isSelectedWhere = where.destinations?.filter(({ active }) => active).length > 0;
    const isSelectedWhat = what?.filter(({ active }) => active).length > 0;

    const { min, max, initial } = budget;
    const { months, endDate, startDate, nights, maxNights, minNights } = when;
    const { adults, children, rooms } = who;

    const isSelectedBudget = min !== initial.min || max !== initial.max;
    const isSelectedWhen = endDate || startDate || months?.length > 0 || nights?.length > 0 || minNights || maxNights;
    const isSelectedWho =
      adults !== who.initial.maxAdults || children !== who.initial.maxChilds || rooms !== who.initial.maxRooms;

    if (
      !showOnboarding &&
      (isSelectedWho ||
        isSelectedWhen ||
        isSelectedBudget ||
        isSelectedWhat ||
        isSelectedWhere ||
        (dealsList.length === 0 && pending)
      ) &&
      isMobile()
    ) {
      dispatch(setShouldScroll(true));
    }
  }, [showOnboarding, where.destinations, what, budget, when, who, dispatch, dealsList.length, pending]);

  useEffect(() => {
    if ((!error && dealsList.length === 0) || search.showLoader || pending || educationalMessage) {
      setShowLoader(true);
    } else {
      setShowLoader(false);
    }

    if (
      !error &&
      !pending &&
      dealsList.length > 0 &&
      nextDeals.length === 0 &&
      pagination.current_offers_page < pagination.total_offers_pages
    ) {
      dispatch(loadMoreDeals());
    }
  }, [dispatch, dealsList, error, pending, search.showLoader, educationalMessage, shouldScroll]);

  useEffect(() => {
    dispatch(clearNextDeals());
  }, [mainFilter.searchEngine, secondaryFilter, shouldScroll]);

  const onLoadMoreClick = () => {
    setShowLoader(true);
    dispatch(updateDeals());
    pushEvent(PAGINATION_CLICKED_EVENT, { countPage });
    setCountPage((prevCount) => prevCount + 1);
  };

  if (!dealsList || (!pending && !error && dealsList.length === 0)) {
    return null;
  }

  const highlightedDeal = dealsList.find((item) => +item.hotel.provider_hotel_id === +selectedHotel.hotelId);

  let dealsArray = [...dealsList];

  if (getParamFromUrl('mainDestinationId') && dealsList.length > 1) {
    const mainDestOffers = dealsList.filter((item, i) => (+item.destinationData.destinationId === +getParamFromUrl('mainDestinationId') && i < 20));
    const otherDestOffers = dealsList.filter((item, i) => (+item.destinationData.destinationId !== +getParamFromUrl('mainDestinationId') && i < 20));
    const otherDestOffers2 = dealsList.filter((item, i) => (i >= 20));
    dealsArray = [
      ...mainDestOffers,
      ...otherDestOffers,
      ...otherDestOffers2
    ];
  }

  if (highlightedDeal) {
    dispatch(setShowLoaderAction(false));

    dealsArray = [
      highlightedDeal,
      ...dealsList.filter((item) => item.offer.offerId !== highlightedDeal.offer.offerId),
    ];
  }

  const activeDestinations = where.destinations?.filter(({ active }) => active).length;
  const isMultipleDestination = activeDestinations > 1 || activeDestinations === 0;
  const isSpecificDates = when.startDate && when.type === 'specific';

  const renderDeals = () => {
    const handleBestOutOfDestination = (destinationId) => {
      return isMultipleDestination && filtersInteractionCount <= 6 ? bestOutOfMultiplied[destinationId] : null;
    };

    return (
      <>
        {[...dealsArray].map((dealItem, i) => (
          <Fragment key={dealItem.offer.offerId + i}>
            {!!(i === 3 && abTesting.dealOfTheDay && dealOfTheDay.length && !dotdPending && !sessionStorage.getItem('hideDealOfTheDay')) && (
              <DealOfTheDay
                dealItem={dealOfTheDay[0]}
                isSpecificDates={isSpecificDates}
                isMultipleDestination={isMultipleDestination}
                hideSettings={false}
                selected={false}
                position={i}
                bestOutOfDestination={handleBestOutOfDestination(dealOfTheDay[0]?.destinationData?.destinationId)}
              />
            )}
            <DealItemExperimental
              key={dealItem.offer.offerId + i}
              dealItem={dealItem}
              isSpecificDates={isSpecificDates}
              isMultipleDestination={isMultipleDestination}
              hideSettings={false}
              selected={+dealItem.hotel.provider_hotel_id === +selectedHotel.hotelId}
              position={i}
              bestOutOfDestination={handleBestOutOfDestination(dealItem.destinationData.destinationId)}
            />
          </Fragment>
        ))}
      </>
    );
  };

  if (pending || !!educationalMessage || dealListLoading) {
    return <DealsLoader rows={12} />;
  }

  return (
    <>
      <CSSTransition
        in={!pending}
        timeout={500}
        unmountOnExit
        classNames="item"
        onExited={() => {
          setShowLoader(true);
        }}
      >
        <>
          {!search.showLoader && !dealListLoading && window.innerWidth > 768 && renderDeals()}
          {!search.showLoader && !dealListLoading && window.innerWidth <= 768 && (
            <MobileListVirtualized
              dealsArray={dealsArray}
              isSpecificDates={isSpecificDates}
              isMultipleDestination={isMultipleDestination}
              selectedHotel={selectedHotel}
              bestOutOfMultiplied={bestOutOfMultiplied}
              filtersInteractionCount={filtersInteractionCount}
              dealOfTheDay={dealOfTheDay}
              dotdPending={dotdPending}
            />
          )}
        </>
      </CSSTransition>

      {((showLoader && !error) || loadMorePending || dealListLoading) && <DealsLoader />}

      {showLoadMoreBtn && isMobile() && <div ref={scrolltoRef} className="scrollto-block" />}

      {showLoadMoreBtn && !isMobile() && (
        <div className="text-center">
          <Button
            type="secondary"
            size="big"
            label={translate('holidayFinder.dealItem.load_more')}
            onClick={onLoadMoreClick}
          />
        </div>
      )}

      <ReactTooltip effect="solid" className="tooltip-container" />
    </>
  );
};

export default React.memo(DealsList);
